/**
 * Build AWS Polly segments from MediKaLLink unread API rows (see ``UnreadMessagesList``).
 */

import type { NormalizedUnreadMessage } from '../types/unreadMessages'

export type InboxReadAloudSegment = {
  text: string
  language: 'en' | 'fr'
}

function ordinalMessageIntro(index: number, lang: 'en' | 'fr'): string {
  const n = index + 1
  if (lang === 'fr') {
    const ord = [
      'Premier',
      'Deuxième',
      'Troisième',
      'Quatrième',
      'Cinquième',
      'Sixième',
      'Septième',
      'Huitième',
      'Neuvième',
      'Dixième',
    ]
    if (n >= 1 && n <= ord.length) return `${ord[n - 1]} message.`
    return `Message numéro ${n}.`
  }
  const ord = [
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
  ]
  if (n >= 1 && n <= ord.length) return `${ord[n - 1]} message.`
  return `Message ${n}.`
}

/** Build Polly segments from MediKaLLink unread rows: ordinal, sender, recipient, subject, body. */
export function segmentsFromUnreadRows(
  rows: ReadonlyArray<NormalizedUnreadMessage>,
  defaultLang: 'en' | 'fr' = 'en',
): InboxReadAloudSegment[] {
  const out: InboxReadAloudSegment[] = []
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const intro = ordinalMessageIntro(i, defaultLang)
    const sender = (r.sender || '').replace(/\s+/g, ' ').trim() || (defaultLang === 'fr' ? 'Expéditeur inconnu' : 'Unknown sender')
    const recipient = (r.recipient || '').replace(/\s+/g, ' ').trim()
    const subject = (r.subject || '').replace(/\s+/g, ' ').trim()
    const rawBody = (r.content || r.preview || '').replace(/<[^>]+>/g, ' ')
    const body = rawBody.replace(/\s+/g, ' ').trim()

    const parts: string[] = [intro]
    if (defaultLang === 'fr') {
      parts.push(recipient ? `De ${sender}, pour ${recipient}.` : `De ${sender}.`)
      if (subject && !/^\(no subject\)$/i.test(subject) && subject !== '(Sans objet)') {
        parts.push(`Objet : ${subject}.`)
      }
      if (body) parts.push(`Contenu du message : ${body}.`)
    } else {
      parts.push(recipient ? `From ${sender}, to ${recipient}.` : `From ${sender}.`)
      if (subject && !/^\(no subject\)$/i.test(subject) && subject !== '(Sans objet)') {
        parts.push(`Subject: ${subject}.`)
      }
      if (body) parts.push(`Message: ${body}.`)
    }

    const text = parts.join(' ').replace(/\s+/g, ' ').trim()
    if (text.length > 1) out.push({ text, language: defaultLang })
  }
  return out
}
